import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox'
import cn from 'classnames'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'

export interface CheckBoxProps {
  label: string
  selected: boolean
  indeterminate: boolean
  disabled?: boolean
  onChanged: (checked: boolean, prop?: string) => void
  value?: string
  className?: string
  labelClassName?: string
  isCheckShow?: boolean
  accordionOpened?: boolean
  tabIndex?: number
}

/**
 * Primary UI component for user interaction
 */
export const CheckBox = React.forwardRef<HTMLDivElement, CheckBoxProps>(
  (
    {
      label,
      selected,
      indeterminate,
      disabled,
      value,
      onChanged,
      className = '',
      labelClassName = '',
      isCheckShow = true,
      accordionOpened = true, // Default to true for backward compatibility
      tabIndex,
      ...props
    },
    ref,
  ) => {
    const capitalizedTitle = label
    const router = useRouter()
    const [isQuery, SetisQuery] = useState<string | undefined>('')
    useEffect(() => {
      const query = window.location.href.split('=')[2]
      SetisQuery(query)
    }, [router])

    const handleClick = () => {
      if (disabled) {
        return
      }
      onChanged(!selected, value)
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        handleClick()
      }
    }

    // Only focusable when accordion is open and not disabled
    const isFocusable = accordionOpened && !disabled
    const computedTabIndex = tabIndex !== undefined ? tabIndex : isFocusable ? 0 : -1

    return (
      <div
        ref={ref}
        className={cn(
          `flex items-start cursor-pointer group ${!isCheckShow && 'w-full h-[42px]'} ${
            disabled ? 'opacity-50 cursor-not-allowed' : ''
          }`,
          className,
        )}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={computedTabIndex}
        role="checkbox"
        aria-checked={selected ? (indeterminate ? 'mixed' : 'true') : 'false'}
        aria-disabled={disabled}
        aria-label={label}
        {...props}
      >
        {isCheckShow && (
          <div className="rounded mr-[5px] w-5 h-5 flex-shrink-0">
            {selected && indeterminate && <CheckBoxIcon sx={{ fontSize: 16 }} />}
            {isQuery && !selected && !indeterminate && <CheckBoxOutlineBlankIcon sx={{ fontSize: 16 }} />}
            {isQuery && !selected && indeterminate && <IndeterminateCheckBoxIcon sx={{ fontSize: 16 }} />}
            {!selected && !isQuery && <CheckBoxOutlineBlankIcon sx={{ fontSize: 16 }} />}
          </div>
        )}
        {labelClassName === '' ? (
          <label
            className={cn(
              `${
                isCheckShow
                  ? 'contents font-primary font-medium text-[15px] text-darkgray text-left'
                  : 'font-secondary font-bold text-[14px] text-white m-auto text-center'
              } flex-shrink-0 cursor-pointer break-words w-full`,
            )}
          >
            {capitalizedTitle}
          </label>
        ) : (
          <label className={cn('', labelClassName)}>{capitalizedTitle}</label>
        )}
      </div>
    )
  },
)

CheckBox.displayName = 'CheckBox'
